import React, { useEffect, useRef, useState } from "react";
import { IonButton, IonContent, IonPage, IonSpinner } from "@ionic/react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router";

import dsLogo from "../../assets/images/ds-logo.svg";
import { getAuthToken } from "../../services/authStorage";
import {
  clearEmailAttempt,
  readEmailAttempt,
  trackEmailAccountOutcome,
  verifySignInLink,
} from "../../services/emailSignIn";
import { toErrorText } from "../../services/errorText";
import { trackFunnel } from "../../services/funnelTracker";
import { LOGIN_PATH, completeSignIn } from "../../services/signInSession";
import "./VerifyEmailLink.css";

const COPY = {
  other_device: {
    title: "Apri il link dal dispositivo giusto",
    text:
      "Il pulsante funziona solo sul telefono o sul browser da cui hai chiesto l'email. " +
      "Qui puoi accedere con il codice che trovi nella stessa email.",
    action: "Accedi con il codice",
  },
  expired: {
    title: "Link scaduto",
    text: "Il link è scaduto o è già stato usato. Richiedi una nuova email per accedere.",
    action: "Richiedi una nuova email",
  },
  error: {
    title: "Qualcosa è andato storto",
    text: "",
    action: "Torna all'accesso",
  },
};

/**
 * Where the VERIFICA EMAIL / ACCEDI button lands. The app opens
 * https://dietasocial.it/app?verifica=… here (CommunityDeepLinkHandler), and the
 * website sends browsers to the web app's /verifica-email?verifica=….
 *
 * Only the app that asked for the email holds the attempt's secret, so the
 * button signs in on that device only. Anywhere else the member is told to use
 * the code from the same email.
 */
const VerifyEmailLinkPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Already signed in, e.g. the same email opened twice.
    if (getAuthToken()) {
      history.replace("/home");
      return;
    }

    trackFunnel("email_link_opened");

    const verifica = new URLSearchParams(location.search).get("verifica");
    const attempt = readEmailAttempt();

    if (!verifica) {
      setStatus("expired");
      return;
    }
    if (!attempt?.secret) {
      setStatus("other_device");
      return;
    }

    (async () => {
      try {
        const { ok, status: httpStatus, data } = await verifySignInLink(
          verifica,
          attempt.secret,
        );

        if (httpStatus === 409 || data?.status === "other_device") {
          setStatus("other_device");
          return;
        }
        if (httpStatus === 410 || data?.status === "expired") {
          clearEmailAttempt();
          setStatus("expired");
          return;
        }
        if (!ok || data?.status !== "registered" || !data?.token) {
          throw new Error(toErrorText(data, "Verifica non riuscita. Riprova."));
        }

        trackFunnel("email_verified", { method: "link" });
        trackEmailAccountOutcome(data);
        clearEmailAttempt();

        const { nextPath } = await completeSignIn({ token: data.token, dispatch });
        history.replace(nextPath);
      } catch (e) {
        setMessage(e?.message || "Verifica non riuscita. Riprova.");
        setStatus("error");
      }
    })();
  }, [dispatch, history, location.search]);

  const copy = COPY[status];

  return (
    <IonPage className="ds-verify-link">
      <IonContent fullscreen>
        <div className="ds-verify-link__wrap">
          <img className="ds-verify-link__logo" src={dsLogo} alt="Dieta Social" />

          {status === "verifying" ? (
            <div className="ds-verify-link__state">
              <IonSpinner name="crescent" color="primary" />
              <p>Verifica in corso…</p>
            </div>
          ) : (
            <div className="ds-verify-link__state">
              <h1>{copy.title}</h1>
              <p>{status === "error" ? message : copy.text}</p>
              <IonButton expand="block" onClick={() => history.replace(LOGIN_PATH)}>
                {copy.action}
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default VerifyEmailLinkPage;
